import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import { ShieldCheck, Lock, KeyRound, X } from "lucide-react";
import PasswordInput from "../components/PasswordInput";

/**
 * Hidden admin portal route at /admin-login.
 * Not linked anywhere on the public site. Admins access via the URL directly.
 * Rejects non-admin accounts and redirects them to /login.
 * Mod 3 — Forgot Password modal sends email + WhatsApp reset link.
 */
export default function AdminLogin() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    // Mod 3 — forgot password modal state
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotBusy, setForgotBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !password) { toast.error("Email and password required"); return; }
        setLoading(true);
        try {
            const u = await login(email.trim().toLowerCase(), password);
            if (!["super_admin", "admin"].includes(u.role)) {
                toast.error("This portal is for admins only.");
                navigate("/login", { replace: true });
                return;
            }
            toast.success(`Welcome, ${u.name}!`);
            navigate("/admin", { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        if (!forgotEmail) { toast.error("Enter your registered admin email"); return; }
        setForgotBusy(true);
        try {
            await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
            setForgotSent(true);
        } catch (err) {
            // Backend always returns 200, but just in case
            toast.error(err.response?.data?.detail || "Could not send reset link");
        } finally {
            setForgotBusy(false);
        }
    };

    const closeForgot = () => {
        setShowForgot(false);
        setForgotEmail("");
        setForgotSent(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6"
            style={{ backgroundImage: "radial-gradient(ellipse at top, rgba(0,255,102,0.06), transparent 60%)" }}>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-lg p-8" data-testid="admin-login-card">
                <div className="flex flex-col items-center mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#00FF66]/10 border border-[#00FF66]/40 flex items-center justify-center mb-3">
                        <ShieldCheck className="w-6 h-6 text-[#00FF66]" />
                    </div>
                    <h1 className="font-display text-2xl font-bold uppercase tracking-widest text-white">Admin Portal</h1>
                    <p className="text-[11px] text-slate-500 mt-1 uppercase tracking-widest">Restricted Access</p>
                </div>

                <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@example.com" data-testid="admin-email-input"
                            autoComplete="off"
                            className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                    </div>
                    <div>
                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Password</label>
                        <div className="mt-2">
                            <PasswordInput value={password} onChange={setPassword}
                                testid="admin-password-input" autoComplete="off" />
                        </div>
                    </div>
                    <button type="submit" disabled={loading} data-testid="admin-login-submit"
                        className="w-full h-11 inline-flex items-center justify-center gap-2 bg-[#00FF66] text-black font-bold uppercase tracking-wide rounded hover:bg-[#00cc52] disabled:opacity-50 glow-green">
                        <Lock className="w-4 h-4" /> {loading ? "Verifying…" : "Sign in to admin"}
                    </button>
                    <div className="text-center pt-1">
                        <button type="button" onClick={() => setShowForgot(true)} data-testid="forgot-password-link"
                            className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-400 hover:text-[#00FF66] font-semibold">
                            <KeyRound className="w-3 h-3" /> Forgot password?
                        </button>
                    </div>
                </form>
                <p className="mt-6 text-[10px] text-slate-600 text-center uppercase tracking-widest">
                    Player or manager? <a href="/login" className="text-slate-400 hover:text-[#00FF66]">Sign in here</a>
                </p>
            </div>

            {/* MOD 3 — Forgot password modal */}
            {showForgot && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-lg p-6 relative" data-testid="forgot-modal">
                        <button onClick={closeForgot} className="absolute top-3 right-3 text-slate-500 hover:text-white" data-testid="forgot-close-btn">
                            <X className="w-4 h-4" />
                        </button>
                        {forgotSent ? (
                            <div className="text-center" data-testid="forgot-sent">
                                <div className="w-12 h-12 mx-auto rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center mb-3">
                                    <KeyRound className="w-5 h-5 text-[#00FF66]" />
                                </div>
                                <h3 className="font-display text-lg font-bold uppercase text-white">Check your inbox</h3>
                                <p className="text-sm text-slate-400 mt-2">
                                    If an admin account exists for <b className="text-white">{forgotEmail}</b>, we've sent a password reset link via Email and WhatsApp. The link expires in 30 minutes.
                                </p>
                                <button onClick={closeForgot} data-testid="forgot-done-btn"
                                    className="mt-5 w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52]">
                                    Done
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submitForgot} className="space-y-4" data-testid="forgot-form">
                                <h3 className="font-display text-lg font-bold uppercase text-white">Forgot Password</h3>
                                <p className="text-xs text-slate-400">Enter your registered admin email. We'll send a reset link via Email + WhatsApp.</p>
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                                    <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                                        placeholder="admin@example.com" data-testid="forgot-email-input" autoComplete="off"
                                        className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                                </div>
                                <button type="submit" disabled={forgotBusy} data-testid="forgot-submit-btn"
                                    className="w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-wide rounded hover:bg-[#00cc52] disabled:opacity-50">
                                    {forgotBusy ? "Sending…" : "Send Reset Link"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
